import PropTypes from 'prop-types';

import { forwardRef, useCallback, useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { faClock, faExclamationCircle, faTimes } from '@fortawesome/pro-solid-svg-icons';

import ViewField from './ViewField';
import * as S from './styled/DateInput.styled';
import { InputMessageContainer } from './styled/InputCommon.styled';

/**
 * @author Burak Unsal
 */
const TimeInput = ({
	name,
	disabled = false,
	viewMode,
	placeholder,
	value,
	setValue,
	inline = false,
	minuteIncrement,
	hourIncrement,
	editableMinute = true,
	time24hr,
	maxTime,
	minTime,
	errorMessage: errorMessageProp,
	...moreProps
}) => {
	const [referenceElement, setReferenceElement] = useState(false);

	const formMethods = useFormContext();
	const errorMessage = formMethods?.getFieldState?.(name, formMethods?.formState)?.error?.message ?? errorMessageProp;

	useEffect(() => {
		const input = referenceElement?.flatpickr?._input;
		const timeContainer = referenceElement?.flatpickr?.timeContainer;
		const minuteInput = timeContainer?.querySelector('input.numInput.flatpickr-minute');

		if (!editableMinute) {
			minuteInput?.addEventListener('keydown', (event) => {
				event.preventDefault();
			});
		}

		if (input) {
			if (placeholder) {
				input.setAttribute('placeholder', placeholder);
			}
		}
		if (timeContainer) {
			timeContainer.addEventListener('mousedown', stopPopper);
			timeContainer.addEventListener('touchend', stopPopper);
		}
		return () => {
			if (timeContainer) {
				timeContainer.removeEventListener('mousedown', stopPopper);
				timeContainer.removeEventListener('touchend', stopPopper);
			}
			if (!editableMinute) {
				minuteInput?.removeEventListener('keydown', (event) => {
					event.preventDefault();
				});
			}
		};
	}, [referenceElement, placeholder]);

	const stopPopper = useCallback((event) => {
		event.stopPropagation();
	}, []);

	const handleInputClick = (event) => {
		event.stopPropagation();

		referenceElement.flatpickr.open();
	};

	const handleDelete = (e) => {
		e.stopPropagation();
		setValue(null);
	};

	return (
		<>
			<S.InputContainer {...moreProps} viewmode={viewMode || disabled}>
				{viewMode || disabled ? (
					<ViewField value={`${value ?? '-'}`} disabled={disabled} />
				) : (
					<S.InputWrapper className="flatpickr" inline={inline} onClick={handleInputClick}>
						<S.IconContainer>
							<Icon icon={faClock} color="var(--content-4)" />
						</S.IconContainer>
						<FlatpickrTimeWrapper
							ref={setReferenceElement}
							value={value}
							setValue={setValue}
							inline={inline}
							error={!!errorMessage}
							minuteIncrement={minuteIncrement}
							time24hr={time24hr}
							maxTime={maxTime}
							minTime={minTime}
							hourIncrement={hourIncrement}
						/>
						{value && (
							<S.IconContainer className="deleteIcon">
								<Icon icon={faTimes} onClick={handleDelete} fontSize="var(--paragraph3-font-size)" />
							</S.IconContainer>
						)}
					</S.InputWrapper>
				)}
			</S.InputContainer>
			{errorMessage && (
				<InputMessageContainer className="error-message-container time-input-error-message-container">
					<Icon icon={faExclamationCircle} color="var(--red)" size="x-small" />
					<Typography variant="caption" color="var(--red)" noWrap>
						{errorMessage}
					</Typography>
				</InputMessageContainer>
			)}
		</>
	);
};

const FlatpickrTimeWrapper = forwardRef((props, ref) => {
	const {
		value,
		setValue,
		inline = false,
		error,
		className,
		minuteIncrement = 5,
		hourIncrement = 1,
		time24hr = true,
		maxTime,
		minTime
	} = props;
	const lang = useSelector((state) => state.layout.localization.code); // This is a hack to force re-render when language changes
	const { getTime, getTimeWithAMPM } = useLocale();

	const [localValue, setLocalValue] = useState(value);

	useUpdateEffect(() => {
		if (JSON.stringify(value) !== JSON.stringify(localValue)) {
			setLocalValue(value);
		}
	}, [value]);

	useUpdateEffect(() => {
		if (JSON.stringify(value) === JSON.stringify(localValue)) {
			return;
		}
		if (localValue !== value) {
			setValue(time24hr ? getTime(localValue) : getTimeWithAMPM(localValue));
		}
	}, [localValue]);

	return (
		<S.FlatpickrTimeContainer className={`${error ? 'error' : ''} ${className ?? ''}`} inline={inline}>
			<Flatpickr
				key={lang}
				ref={ref}
				value={localValue}
				onChange={setLocalValue}
				options={{
					enableTime: true,
					noCalendar: true,
					dateFormat: `${time24hr ? 'H:i' : 'h:iK'}`,
					// eslint-disable-next-line camelcase
					time_24hr: time24hr,
					disableMobile: 'true',
					minuteIncrement,
					hourIncrement,
					maxTime,
					minTime
				}}
			/>
		</S.FlatpickrTimeContainer>
	);
});

FlatpickrTimeWrapper.displayName = 'FlatpickrTimeWrapper';

TimeInput.propTypes = {
	/** Disables onclick event and UI true/false */
	disabled: PropTypes.bool,
	/** To be able to change the requirement of the input */
	required: PropTypes.bool,
	/** Label text, if it's empty only input will be shown */
	label: PropTypes.string,
	/** Description of the date input field.*/
	description: PropTypes.string,
	/** Enables view mode and instead of input we will show typography*/
	viewMode: PropTypes.bool,
	/** What should appear in the input when no value is selected ? */
	placeholder: PropTypes.string,
	/** The text that will appear in the error state */
	errorMessage: PropTypes.string,
	/** The value of the input */
	value: PropTypes.any,
	/** The function that will be called when the value of the input changes */
	setValue: PropTypes.func,
	/** Inputs loading state */
	isLoading: PropTypes.bool,
	/** Inline mode */
	inline: PropTypes.bool
};

export default TimeInput;
